import { flit, flitFlyer } from "./games/flit";
import { nibble, nibbleChase } from "./games/nibble";
import { pindrop, pindropSteady } from "./games/pindrop";
import { sorter, sorterTidy } from "./games/sorter";
import { tumble, tumbleRobo } from "./games/tumble";
import { zoom, zoomRunner } from "./games/zoom";
import type { InputEvent, RealtimeGame, RealtimeState } from "./realtime";

/**
 * The arcade games, kept in their own list rather than added to `GAME_LIST`.
 *
 * A `GameDefinition` is two players alternating against one shared state; a
 * `RealtimeGame` is one player's run, simulated locally and checked afterwards.
 * They are different enough that a single registry would have to be a union
 * everything downstream then has to unpick — the room, the picker, the bot, the
 * stats. Two lists cost one extra field on the room and keep both honest.
 */

export const ARCADE_IDS = ["nibble", "tumble", "flit", "sorter", "pindrop", "zoom"] as const;
export type ArcadeId = (typeof ARCADE_IDS)[number];

export function isArcadeId(value: unknown): value is ArcadeId {
	return typeof value === "string" && (ARCADE_IDS as readonly string[]).includes(value);
}

export interface ArcadeEntry {
	id: ArcadeId;
	title: string;
	emoji: string;
	blurb: string;
	howTo: readonly string[];
	// biome-ignore lint/suspicious/noExplicitAny: one registry, many state shapes
	game: RealtimeGame<any, any>;
	/**
	 * How Robo plays this one, made fresh for each run.
	 *
	 * A factory because a policy may want to remember what it decided — the
	 * falling-blocks one plans a placement per piece rather than re-searching
	 * every step — and two runs must not share that. It lives here rather than
	 * in the room so that adding a game means adding a line to this list and
	 * nothing at all on the server.
	 */
	// biome-ignore lint/suspicious/noExplicitAny: one registry, many input shapes
	bot: () => (state: any) => any;
}

export const ARCADE_LIST: readonly ArcadeEntry[] = [
	{
		id: "nibble",
		title: "Nibble",
		emoji: "🐍",
		blurb: "Eat the apples and don't bump into anything. Same board, best score wins.",
		howTo: [
			"Tap the left and right buttons to turn — you never stop moving.",
			"Every apple makes you longer, and longer is harder.",
			"You both get the very same apples, so it is a fair race.",
		],
		game: nibble,
		bot: () => nibbleChase,
	},
	{
		id: "tumble",
		title: "Tumble",
		emoji: "🧱",
		blurb: "Fit the falling blocks together and clear full rows. Same pieces, best score wins.",
		howTo: [
			"Slide the block left and right, and turn it with the arrow button.",
			"Fill a whole row across and it disappears — four at once is worth the most.",
			"You both get the very same blocks in the very same order.",
		],
		game: tumble,
		bot: tumbleRobo,
	},
	{
		id: "flit",
		title: "Flit",
		emoji: "🐦",
		blurb: "One button, and a lot of pipes. Same pipes for both of you — get further.",
		howTo: [
			"Tap anywhere to flap. Stop tapping and you drop like a stone.",
			"Go through the gaps. Touching a pipe or the ground ends your go.",
			"You both fly the very same pipes, so it comes down to who flies better.",
		],
		game: flit,
		bot: () => flitFlyer,
	},
	{
		id: "sorter",
		title: "Colour Sort",
		emoji: "🧪",
		blurb: "Pour the tubes until each one holds a single colour. Same levels, most solved wins.",
		howTo: [
			"Tap a tube to pick it up, then tap another to pour it in.",
			"Colours only pour onto the same colour, or into an empty tube.",
			"Finish a level and the next one appears. You both get the same ones.",
		],
		game: sorter,
		bot: () => sorterTidy,
	},
	{
		id: "pindrop",
		title: "Pin Drop",
		emoji: "📌",
		blurb: "Pull the pins in the right order so the colours land together. Most cups wins.",
		howTo: [
			"Each pin holds some balls. Pull one and they drop into the tray.",
			"The tray fills from the left, so the order you pull in is everything.",
			"A cup of four of one colour is a point. Same pins for both of you.",
		],
		game: pindrop,
		bot: () => pindropSteady,
	},
	{
		id: "zoom",
		title: "Zoom",
		emoji: "🚀",
		blurb: "Race down a tunnel in 3D. Dodge, jump, grab the gems. Same tunnel, best score wins.",
		howTo: [
			"Swipe left and right to change lane, and swipe up or tap to jump.",
			"Walls have to be gone round and bars have to be jumped — some rows want both.",
			"It gets faster the longer you last. You both run the very same tunnel.",
		],
		game: zoom,
		bot: zoomRunner,
	},
];

export function getArcade(id: ArcadeId): ArcadeEntry {
	const entry = ARCADE_LIST.find((candidate) => candidate.id === id);
	if (!entry) throw new Error(`no arcade game ${id}`);
	return entry;
}

/** What one player got, once the server has replayed their log. */
export interface ArcadeRun {
	score: number;
	steps: number;
	/** True when they were still alive when the clock ran out. */
	survived: boolean;
}

/** The arcade half of a room, or null when an ordinary game is on. */
export interface ArcadeView {
	arcadeId: ArcadeId;
	/**
	 * The same for both players, which is what makes it a race rather than two
	 * unrelated games. Not secret — knowing where the apples are is knowing the
	 * board, and both children can see it anyway.
	 */
	seed: number;
	runs: [ArcadeRun | null, ArcadeRun | null];
}

/** Sent up when a run ends. A list of presses, and nothing else. */
export interface RunSubmission<I = unknown> {
	log: InputEvent<I>[];
}

/**
 * Who won, once both runs are in. Null while anybody is still playing, and a
 * null winner inside the result means they tied.
 */
export function arcadeWinner(view: ArcadeView): { winner: 0 | 1 | null } | null {
	const [first, second] = view.runs;
	if (!first || !second) return null;
	if (first.score === second.score) {
		// Same score: whoever lasted longer was playing better for longer.
		if (first.steps === second.steps) return { winner: null };
		return { winner: first.steps > second.steps ? 0 : 1 };
	}
	return { winner: first.score > second.score ? 0 : 1 };
}

export type { InputEvent, RealtimeState };
